import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from '@/components/ui/sonner';
import { empresaService } from '../services/empresaService';
import type { AtualizarEmpresaRequest } from '../types';

export function useEmpresas() {
  return useQuery({
    queryKey: ['empresas'],
    queryFn: empresaService.listar,
    staleTime: 60_000,
  });
}

/**
 * Ramo de atividade da empresa logada, para o menu esconder o que não é do
 * negócio. `undefined` enquanto carrega; `null` se não deu para ler (API antiga
 * ou erro) — aí o menu mostra tudo, como antes.
 */
export function useTipoEmpresaAtual(empresaId: number | undefined): number | null | undefined {
  const { data, isError } = useQuery({
    queryKey: ['empresa-tipo-atual', empresaId],
    queryFn: empresaService.tipoAtual,
    enabled: !!empresaId,
    staleTime: 5 * 60_000,
    retry: 1,
  });
  if (!empresaId || isError) return null;
  return data;
}

export function useEmpresa(id: number | undefined) {
  const queryClient = useQueryClient();

  const { data: empresa, isLoading } = useQuery({
    queryKey: ['empresa', id],
    queryFn: () => empresaService.getById(id!),
    enabled: !!id,
    staleTime: 30_000,
  });

  const { mutate: atualizar, isPending: isSalvando } = useMutation({
    mutationFn: (req: AtualizarEmpresaRequest) => empresaService.atualizar(id!, req),
    onSuccess: (aviso) => {
      queryClient.invalidateQueries({ queryKey: ['empresa', id] });
      queryClient.invalidateQueries({ queryKey: ['empresas'] });
      // A aba Emitente mostra os dados deste cadastro.
      queryClient.invalidateQueries({ queryKey: ['emitente-fiscal', id] });
      queryClient.invalidateQueries({ queryKey: ['empresa-tipo-atual'] });
      if (aviso) toast.warning(aviso, { duration: 12_000 });
      else toast.success('Dados da empresa salvos com sucesso!');
    },
    onError: () => toast.error('Erro ao salvar dados da empresa.'),
  });

  return { empresa, isLoading, atualizar, isSalvando };
}
